// src/components/ConcatenateVideosTab.tsx - 修正版
import React, { useState, ChangeEvent } from 'react';

interface ConcatenateVideosTabProps {
  onProcess: (videoFiles: File[]) => Promise<Blob | null>;
  isLoading: boolean;
  message: string;
}

const ConcatenateVideosTab: React.FC<ConcatenateVideosTabProps> = ({ onProcess, isLoading, message }) => {
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [videoPreviews, setVideoPreviews] = useState<string[]>([]);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [outputLoaded, setOutputLoaded] = useState<boolean>(false);

  // 動画ファイル選択ハンドラ - 修正
  const handleVideoChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    console.log("選択されたファイル数:", e.target.files.length);
    
    // FileListをArrayに変換
    const files = Array.from(e.target.files);
    
    // 既存のプレビューをクリーンアップ
    videoPreviews.forEach(url => URL.revokeObjectURL(url));
    
    // 新しいプレビューURLを作成
    const previews = files.map(file => URL.createObjectURL(file));
    
    setVideoFiles(files);
    setVideoPreviews(previews);
    
    // 既存の出力をクリア
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
      setOutputUrl(null);
      setOutputLoaded(false);
    }
  };
  
  // 個別のファイル追加ハンドラを追加
  const handleAddVideo = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    const newFiles = [...videoFiles, file];
    
    // プレビューURLの作成
    const previewUrl = URL.createObjectURL(file);
    const newPreviews = [...videoPreviews, previewUrl];
    
    setVideoFiles(newFiles);
    setVideoPreviews(newPreviews);
    
    // フォームをリセットして同じファイルを再選択できるようにする
    e.target.value = "";
  };

  // 動画の並び替え
  const moveVideo = (index: number, direction: number): void => {
    if (
      (direction === -1 && index === 0) || 
      (direction === 1 && index === videoFiles.length - 1)
    ) {
      return;
    }
    
    const newFiles = [...videoFiles];
    const newPreviews = [...videoPreviews];
    
    // 要素を入れ替え
    const tempFile = newFiles[index];
    newFiles[index] = newFiles[index + direction];
    newFiles[index + direction] = tempFile;
    
    const tempPreview = newPreviews[index];
    newPreviews[index] = newPreviews[index + direction];
    newPreviews[index + direction] = tempPreview;
    
    setVideoFiles(newFiles);
    setVideoPreviews(newPreviews);
  };

  // 動画の削除
  const removeVideo = (index: number): void => {
    const newFiles = [...videoFiles];
    const newPreviews = [...videoPreviews];
    
    // 削除するプレビューのURLを解放
    URL.revokeObjectURL(newPreviews[index]);
    
    // 配列から要素を削除
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setVideoFiles(newFiles);
    setVideoPreviews(newPreviews);
  };

  // 処理実行ハンドラ
  const handleProcess = async (): Promise<void> => {
    if (videoFiles.length < 2) {
      alert('連結するには少なくとも2つの動画ファイルが必要です');
      return;
    }
    
    try {
      const outputBlob = await onProcess(videoFiles);
      if (outputBlob) {
        // 既存のURLを解放
        if (outputUrl) {
          URL.revokeObjectURL(outputUrl);
        }
        
        // 新しい出力のURLを作成
        const url = URL.createObjectURL(outputBlob);
        setOutputUrl(url);
        setOutputLoaded(false);
      }
    } catch (error) {
      console.error('処理エラー:', error);
      alert(`処理中にエラーが発生しました: ${(error as Error).message}`);
    }
  };
  
  return (
    <div className="tab-pane">
      <h3 className="text-lg font-semibold mb-4">複数の動画を連結</h3>
      <p className="mb-6 text-gray-600">
        複数の動画ファイルを選択して、1つの動画に連結します。順序を変更するには矢印ボタンを使用してください。
      </p>
      
      {/* ファイル選択UIを変更 */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium mb-2">動画ファイルを追加:</h4>
        <input 
          type="file" 
          onChange={handleAddVideo}
          accept="video/mp4,video/webm" 
          disabled={isLoading}
          className="w-full mb-4"
        />
        <p className="text-sm text-gray-500">
          ※ 1つずつファイルを追加できます。複数のファイルを連結するには、複数回追加してください。
        </p>
      </div>
      
      {videoFiles.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium mb-2">選択された動画 ({videoFiles.length})</h4>
          
          <div className="space-y-4">
            {videoFiles.map((file, index) => (
              <div key={index} className="flex bg-gray-50 rounded-md p-3">
                <div className="w-1/3 mr-4">
                  <video 
                    src={videoPreviews[index]} 
                    controls 
                    className="w-full h-24 object-contain bg-black rounded-md"
                  />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm mb-2 truncate">{file.name}</div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => moveVideo(index, -1)}
                      disabled={index === 0}
                      title="上に移動"
                      className={`px-2 py-1 text-xs rounded ${
                        index === 0 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      ↑
                    </button>
                    <button 
                      onClick={() => moveVideo(index, 1)}
                      disabled={index === videoFiles.length - 1}
                      title="下に移動"
                      className={`px-2 py-1 text-xs rounded ${
                        index === videoFiles.length - 1 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      ↓
                    </button>
                    <button 
                      onClick={() => removeVideo(index)}
                      title="削除"
                      className="px-2 py-1 text-xs rounded bg-red-100 hover:bg-red-200 text-red-700"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex flex-col items-center mb-6">
        <button 
          onClick={handleProcess} 
          disabled={isLoading || videoFiles.length < 2}
          className={`px-6 py-2 rounded-md font-medium ${
            isLoading || videoFiles.length < 2 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? '処理中...' : '動画を連結'}
        </button>
        
        {message && (
          <p className="mt-4 text-center text-sm">
            {message}
          </p>
        )}
      </div>
      
      {outputUrl && (
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold mb-4">連結された動画:</h3>
          <div className="bg-black rounded-lg overflow-hidden relative">
            <video 
              src={outputUrl} 
              controls 
              className="w-full"
              onLoadedData={() => setOutputLoaded(true)}
            />
            {!outputLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white">
                読み込み中...
              </div>
            )}
          </div>
          <div className="flex justify-center mt-4">
            <a 
              href={outputUrl} 
              download="concatenated-video.mp4" 
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              ダウンロード
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcatenateVideosTab;